import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import hero from "@/assets/hero-fast.webp";
import heroMobile from "@/assets/hero-mobile-fast.webp";
import chiSiamoHome from "@/assets/chi-siamo-home.jpg";
import abatinaLogo from "@/assets/logo-abatina.webp";
import abatinaSeccaImg from "@/assets/abatina-secca.webp";
import abatinaFrescaImg from "@/assets/abatina-fresca.webp";
import { ProductCard } from "@/components/ProductCard";
import { SafeImage } from "@/components/SafeImage";
import { CertificationsStrip } from "@/components/CertificationsStrip";
import { ReviewsMarquee } from "@/components/ReviewsMarquee";
import { Reveal } from "@/components/Reveal";
import { Eyebrow } from "@/components/Eyebrow";
import { SectionTitle } from "@/components/SectionTitle";
import { listPublicProducts } from "@/lib/public.functions";
import { useProductsHidden } from "@/hooks/useSiteSettings";
import { KnifeIcon, LeafIcon, MedalIcon, HeartIcon } from "@/components/icons/BrandIcons";
import { ArrowRight, Phone, MessageCircle } from "lucide-react";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Macelleria artigianale a Campobasso | Bottega da Marco" },
      { name: "description", content: "Carni frollate, salumi tradizionali e L'Abatina (Slow Food). Bottega indipendente nel cuore di Campobasso, in Via Kennedy 1." },
      { property: "og:title", content: "Macelleria artigianale a Campobasso | Bottega da Marco" },
       { property: "og:description", content: "Carni frollate dal Molise. Salumi tradizionali e L'Abatina, presidio Slow Food, dal cuore di Campobasso." },
      { property: "og:url", content: "https://labottegadellecarnidamarco.it/" },
    ],
    links: [
      { rel: "canonical", href: "https://labottegadellecarnidamarco.it/" },
      { rel: "preload", as: "image", href: heroMobile, media: "(max-width: 767px)", fetchPriority: "high" },
      { rel: "preload", as: "image", href: hero, media: "(min-width: 768px)", fetchPriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Dove si trova La Bottega delle Carni da Marco?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "La bottega si trova a Campobasso, in Via John Fitzgerald Kennedy 1, all'interno del supermercato Tigre. È una bottega di carni completamente indipendente.",
              },
            },
            {
              "@type": "Question",
              name: "Quali sono gli orari della macelleria?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Siamo aperti dal lunedì al sabato, dalle 8:00 alle 13:30 e dalle 16:00 alle 20:00. La domenica siamo chiusi, ma è possibile ritirare gli ordini prenotati in precedenza.",
              },
            },
            {
              "@type": "Question",
              name: "Che cos'è L'Abatina?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "L'Abatina è il salume storico di Campobasso, nato nel quartiere della Chiesa di Sant'Antonio Abate. È riconosciuto da Slow Food (Arca del Gusto) e iscritto nel registro De.Co. del Comune di Campobasso. Lo produciamo in due varianti: secca e fresca.",
              },
            },
            {
              "@type": "Question",
              name: "È possibile prenotare la carne e ritirarla la domenica?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Sì. Prenotando entro il sabato (o anche prima) tramite telefono, WhatsApp o la pagina Richieste del sito, prepariamo l'ordine e potete ritirarlo anche la domenica mattina.",
              },
            },
            {
              "@type": "Question",
              name: "Da dove proviene la carne che vendete?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Selezioniamo carni molisane da allevamenti che conosciamo direttamente, con filiera tracciata, e le lavoriamo in bottega con frollatura e stagionatura tradizionali.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: HomePage,
});

const VALUES = [
  { icon: KnifeIcon, title: "Tradizione", text: "Tecniche tramandate da generazioni" },
  { icon: LeafIcon, title: "Qualità", text: "Solo carni molisane selezionate" },
  { icon: MedalIcon, title: "Riconoscimenti", text: "Slow Food, F.I.M., De.Co." },
   { icon: HeartIcon, title: "Passione", text: "Artisti della carne" },
];

function HomePage() {
  const productsHidden = useProductsHidden();
  const { data } = useQuery({
    queryKey: ["public-products"],
    queryFn: () => listPublicProducts(),
  });
  const PRODUCTS = data?.products ?? [];
  const FEATURED = useMemo(() => PRODUCTS.filter((p) => p.featured).slice(0, 4), [PRODUCTS]);
  const OTHERS = useMemo(() => PRODUCTS.filter((p) => !p.featured).slice(0, 9), [PRODUCTS]);
  const abatinaFresca = PRODUCTS.find((p) => p.slug === "abatina-fresca");
  const abatinaSecca = PRODUCTS.find((p) => p.slug === "abatina-secca");
  return (
    <>
      {/* HERO */}
      <section className="relative min-h-screen flex items-end pt-[4.76rem] paper-texture overflow-hidden">
        <picture className="absolute inset-x-0 bottom-0 top-16 sm:top-20 xl:top-24 w-full">
          <source media="(min-width: 768px)" srcSet={hero} />
          <img
            src={heroMobile}
            alt="Marco al banco della bottega"
            width={243}
            height={406}
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover object-top"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-charcoal/85 via-brand-charcoal/40 to-transparent" />
        <div className="container mx-auto px-4 pb-20 md:pb-28 relative">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="max-w-2xl">
            <h1 className="font-display text-white text-4xl md:text-[3.6rem] leading-[1.02]">
              L'arte della carne,<br/>da generazioni.
            </h1>
            <p className="text-white/85 text-base mt-6 max-w-xl leading-relaxed">
              Carni frollate, salumi tradizionali e <em className="font-italic-serif text-brand-gold">L'Abatina</em>, il salume storico riconosciuto da Slow Food. <strong className="text-white">Bottega indipendente</strong> all'interno del supermercato <strong className="text-white">Tigre</strong> di Via Kennedy, Campobasso.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              {!productsHidden && (
                <Link to="/prodotti" className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-red-hover text-white px-6 py-3.5 rounded-md text-sm uppercase tracking-[0.16em]">
                  Scopri i prodotti <ArrowRight size={16}/>
                </Link>
              )}
              <Link to="/chi-siamo" className="inline-flex items-center gap-2 border border-white/40 hover:border-white text-white px-6 py-3.5 rounded-md text-sm uppercase tracking-[0.16em] backdrop-blur">
                La nostra storia
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALORI */}
      <section className="bg-brand-cream py-14 border-y border-brand-gold/20">
        <div className="container mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0 md:divide-x divide-brand-gold/30">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.05}>
              <div className="text-center md:px-6">
                <v.icon size={32} className="mx-auto text-brand-red" />
                <h2 className="font-display text-xl mt-3">{v.title}</h2>
                <p className="text-sm text-brand-warm-gray mt-1">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      {!productsHidden && <section className="pt-8 pb-12 md:py-20">
        <div className="container mx-auto px-4">
          <Reveal>
            <div className="text-center mb-3"><Eyebrow>Selezione</Eyebrow></div>
            <SectionTitle align="center">I nostri prodotti in evidenza</SectionTitle>
            <p className="text-center font-italic-serif italic text-brand-warm-gray mt-3 text-lg">
              Best seller e selezioni speciali del macellaio
            </p>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mt-6 md:mt-10">
            {FEATURED.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.05}>
                <ProductCard product={p} compact />
              </Reveal>
            ))}
          </div>

          <div className="text-right mt-6">
            <Link to="/prodotti" className="inline-flex items-center gap-1 text-brand-red text-sm uppercase tracking-[0.16em] hover:gap-2 transition-all">
              Vedi tutti i prodotti <ArrowRight size={14}/>
            </Link>
          </div>
        </div>
      </section>}

      {/* ABATINA SHOWCASE */}
      <section className="bg-brand-charcoal text-white py-20">
        <div className="container mx-auto px-4 grid md:grid-cols-5 gap-6 md:gap-10 items-center">
          <div className="md:col-span-2 text-center md:text-left">
            <img src={abatinaLogo} alt="L'Abatina" width={1000} height={554} loading="lazy" decoding="async" className="h-auto w-full max-w-[18rem] sm:max-w-[22rem] md:max-w-full -mt-4 md:mt-0 mb-2 md:mb-6 mx-auto md:mx-0 drop-shadow-[0_6px_24px_rgba(0,0,0,0.45)]" />
            <p className="text-brand-cream/80 mt-4 leading-relaxed max-w-md">
              Il nostro salume storico, riconosciuto da Slow Food. Una ricetta segreta che nasce a Campobasso negli anni '50, presso la Chiesa di Sant'Antonio Abate. Lo produciamo ancora oggi seguendo la tradizione: in due varianti, secca e fresca.
            </p>
          </div>
          <div className="md:col-span-3 grid grid-cols-2 gap-4">
            {[
              { to: "/abatina#fresca", img: abatinaFresca?.image || abatinaFrescaImg, label: "Abatina Fresca" },
              { to: "/abatina#secca", img: abatinaSecca?.image || abatinaSeccaImg, label: "Abatina Secca" },
            ].map((it) => (
              <Link key={it.label} to="/abatina" hash={it.to.split("#")[1]} className="relative aspect-square overflow-hidden rounded-md group zoom-img-parent">
                <SafeImage src={it.img} alt={it.label} className="zoom-img w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10 flex items-end p-5">
                  <div>
                    <div className="font-display text-2xl text-white">{it.label}</div>
                    <div className="text-brand-gold text-xs uppercase tracking-[0.2em] mt-1 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      Scopri <ArrowRight size={12}/>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="md:col-span-5 text-center mt-4">
            <Link to="/abatina" className="inline-flex items-center gap-2 border border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-brand-charcoal px-6 py-3 rounded-md text-sm uppercase tracking-[0.16em] transition-colors">
              Scopri la storia dell'Abatina <ArrowRight size={14}/>
            </Link>
          </div>
        </div>
      </section>

      {/* STORIA preview */}
      <section className="py-20">
        <div className="container mx-auto px-4 grid md:grid-cols-2 gap-10 items-stretch">
          <Reveal className="h-full">
            <div className="h-full min-h-[420px] overflow-hidden rounded-md zoom-img-parent">
              <img src={chiSiamoHome} alt="Marco al lavoro nella bottega" className="zoom-img w-full h-full object-cover"/>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="self-center">
            <Eyebrow>La nostra storia</Eyebrow>
            <SectionTitle className="mt-3">Da generazioni nel cuore del Molise</SectionTitle>
            <div className="mt-6 space-y-4 text-brand-warm-gray leading-[1.8]">
              <p>Da decenni dedichiamo le nostre giornate a un mestiere antico che richiede tempo, conoscenza e mani esperte. La Bottega delle Carni da Marco è nata a Campobasso con un'unica missione: offrire ai molisani carne di alta qualità, lavorata come una volta.</p>
              <p>Non siamo solo una macelleria. Siamo una bottega di pregiate carni e prodotti selezionati. Marco e il suo team sono artisti della carne: ogni taglio, ogni preparazione, ogni salume nasce da una passione che si tramanda.</p>
              <p>Operiamo all'interno del Tigre di Via Kennedy, ma siamo una bottega completamente indipendente. La nostra filiera, le nostre tecniche, la nostra storia: tutto è nostro.</p>
            </div>
            <Link to="/chi-siamo" className="mt-6 inline-flex items-center gap-2 border border-brand-charcoal/30 hover:border-brand-red hover:text-brand-red px-5 py-3 rounded-md text-sm uppercase tracking-[0.16em] transition-colors">
              Scopri di più <ArrowRight size={14}/>
            </Link>
          </Reveal>
        </div>
      </section>

      <CertificationsStrip />

      {/* ALTRI PRODOTTI */}
      {!productsHidden && <section className="py-20">
        <div className="container mx-auto px-4">
          <Reveal>
            <SectionTitle align="center">La nostra selezione</SectionTitle>
            <p className="text-center font-italic-serif italic text-brand-warm-gray mt-3 text-lg">
              Tutto ciò che la bottega offre, dalla carne fresca ai preparati
            </p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mt-10">
            {OTHERS.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.04}>
                <ProductCard product={p}/>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/prodotti" className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-red-hover text-white px-7 py-4 rounded-md text-sm uppercase tracking-[0.16em]">
              Vedi il nostro bancone <ArrowRight size={14}/>
            </Link>
          </div>
        </div>
      </section>}

      {/* RITIRO DOMENICA */}
      <section className="bg-brand-red text-white py-20 paper-texture">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <Eyebrow color="cream">Su prenotazione</Eyebrow>
          <h3 className="font-display text-white text-3xl md:text-4xl mt-3">
            Ordini personalizzati e ritiro la domenica
          </h3>
          <p className="mt-5 text-white/90 leading-relaxed">
            Vuoi una carne particolare per il pranzo della domenica? Prenota il sabato (o anche prima) lasciando il tuo nome: ti prepariamo tutto e ritiri anche la domenica mattina.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <a href="tel:+393407487310" className="inline-flex items-center gap-2 bg-white text-brand-red px-6 py-3 rounded-md text-sm uppercase tracking-[0.16em] hover:bg-brand-cream">
              <Phone size={16}/> Chiama ora
            </a>
            <a href="https://wa.me/393407487310" className="inline-flex items-center gap-2 border border-white/60 hover:bg-white/10 px-6 py-3 rounded-md text-sm uppercase tracking-[0.16em]">
              <MessageCircle size={16}/> WhatsApp
            </a>
          </div>
        </div>
      </section>

      <ReviewsMarquee />
    </>
  );
}
